import { useT, useLocalizedName, useRecurrenceDelete, useRecurrenceEdit, useDebounce } from "@hooks";
import { useIntl } from "react-intl";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ExpenseForm from "./form";
import { TableExpenses } from "@components";
import { MonthNavigation } from "@components/Dashboard";
import moment from "moment";
import Dialog from "@rio-cloud/rio-uikit/Dialog";
import DialogCreateType from "@components/Dialog/Create";
import DialogObservation from "@components/Dialog/Observation";
import DialogAudit from "@components/Dialog/Audit";
import RecurrenceScopeDialog from "@components/Dialog/RecurrenceScope";
import DialogRecurrenceDetails from "@components/Dialog/RecurrenceDetails";
import { Complement, criarComplementoId } from "@components/shared/ComplementList";
import {
  createBranch,
  createCategory,
  createSegments,
  createSubCategories,
  createSuppliers,
  createTransaction,
  createSupplyTransaction,
  deleteFile,
  deleteTransaction,
  editTransaction,
  exportTransactions,
  getDetailTransaction,
  getDRESummary,
  getFile,
  getTableData,
  inactivateBranch,
  inactivateSegments,
  inactivateSuppliers,
  inactivateCategory,
  inactivateSubCategories,
  createOdometerTransaction,
  getOdometerTransaction,
  deleteOdometerTransaction,
  putOdometerTransaction,
  getPaymentMethods,
  createPaymentMethod,
} from "@services/form";
import { Notification } from "@rio-cloud/rio-uikit";
import { FormState, useExpensesForm } from "./useExpensesForm";
import ReactivateDialog, { InactiveItem } from "@components/Dialog/Reactivate";
import { InactiveSelectOption } from "@components/Dialog/Receipt/select";
import { getTransactionSaveErrorLabelKey } from "@utils/apiError";
import { resolveTransactionStatus } from "@utils/transactionStatus";
import {
  buildRecurrencePayload,
  parseRecurrenceFromApi,
  toBackendStatus,
  toFormDate,
} from "@utils/transactionRecurrence";
import { ExpenseStatus, Vehicle } from "@components/Table/Expenses/Table";
import { formatValue, normalizeText, parseCurrencyBRL } from "@utils";
import DialogCreateCategory from "@components/Dialog/CreateCategory";
import SelectedFilters from "@components/SelectedFilters";
import KpiCardsSkeleton from "@components/Skeletons/KpiCardsSkeleton";
import { ITransactionMaintenance, ITransactionPayload, ISupplyTransactionPayload, ISupplyTrucker } from "@types";
import { setEndDate, setStartDate } from "@store/date/dateSlice";
import { useAppDispatch } from "@store";
import TransactionKpiCard, { CURRENCY_KPI_ICON, calculateDelta } from "@screens/shared/TransactionKpiCard";
import KpiInfoTooltip, { formatPeriodRange } from "@components/shared/KpiInfoTooltip";
import ConfirmDeleteDialog from "@screens/shared/ConfirmDeleteDialog";
import {
  MAX_DRIVERS,
  resolveMomentLocale,
  resolveStatusAmounts,
  resolveTripDriverId,
  TransactionTableItem,
  type TransactionStatusPeriod,
} from "@screens/shared/transactionScreenUtils";
import { resolveTripLabel } from "@utils/rioTripOptions";
import { resolvePaymentMethodLabel } from "@utils/paymentMethodLabel";
import { isMaintenanceCategoryName, resolveMaintenanceTypeFromSubcategory } from "@utils/maintenanceType";
import { useTransactionFilters } from "@screens/shared/useTransactionFilters";
import { useTransactionListData } from "@screens/shared/useTransactionListData";
import { useMonthPeriodNavigation } from "@screens/shared/useMonthPeriodNavigation";

export type NameInputChange =
  | "date"
  | "value"
  | "branch"
  | "segment"
  | "supplier"
  | "trip"
  | "vehicle"
  | "driver"
  | "category"
  | "subcategory"
  | "taxes"
  | "observation"
  | "hour"
  | "tollLocation"
  | "concessionaire"
  | "plaza"
  | "tagType"
  | "tagNumber"
  | "paymentMethod"
  | "status"
  | "repeatEvery"
  | "recurrenceStart"
  | "recurrenceEnd"
  | "maintenanceType";

interface Option {
  [x: string]: { placeholder: string; title: string };
}

interface EditingMaintenance {
  block: ITransactionMaintenance | null;
  initialType: string;
}

const EMPTY_EDITING_MAINTENANCE: EditingMaintenance = { block: null, initialType: "" };

const defaultOption = {
  open: false,
  type: "",
  title: "",
  value: "",
  placeholder: "",
};

const SEARCH_DEBOUNCE = 2000;

const formatKpiComparisonCurrency = (value: number) =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const optionLabelToText = (label: ReactNode): string => (typeof label === "string" ? label : "");

const extractDatePart = (value: string): string => (value.includes("T") ? value.split("T")[0] : value);

const extractDateFromDateTime = (dateTime?: string): string => dateTime?.split("T")[0] || "";

const extractTimeFromDateTime = (dateTime?: string): string => {
  if (!dateTime) return "";
  const timePart = dateTime.split("T")[1];
  if (!timePart) return "";
  return timePart.substring(0, 5);
};

const splitTollDateTime = (value?: string): { date: string; time: string } => {
  if (!value) return { date: "", time: "" };
  const normalized = value.includes("T") ? value : value.replace(" ", "T");
  const [datePart, timePart = ""] = normalized.split("T");
  return { date: datePart, time: timePart.substring(0, 5) };
};

const resolveEditDrivers = (expense: any, driversOptions: { id: string; rioDriverId?: string }[]): string[] => {
  const driversFromApi = expense.driverIds || (expense.driverId ? [expense.driverId] : []);
  const rioDriverIds: string[] = Array.isArray(driversFromApi) ? driversFromApi.slice(0, MAX_DRIVERS) : [];
  return rioDriverIds.map((rioId) => {
    const match = driversOptions.find((o) => o.rioDriverId === rioId);
    return match ? String(match.id) : rioId;
  });
};

const resolveEditVehicleId = (expense: any, vehiclesOptions: { id: string; label: ReactNode }[]): string => {
  const vehicleIdFromApi = expense.vehicleId?.toString() || "";
  if (vehicleIdFromApi || !expense.vehiclePlate) return vehicleIdFromApi;

  const match = vehiclesOptions.find(
    (v) => optionLabelToText(v.label).toUpperCase() === String(expense.vehiclePlate).toUpperCase()
  );
  return match ? String(match.id) : vehicleIdFromApi;
};

const parseComplementsFromApi = (rawComplements: unknown): Complement[] => {
  if (!Array.isArray(rawComplements)) return [];

  return rawComplements.map((c: any) => ({
    id: criarComplementoId(),
    type: c.type ? String(c.type).toLowerCase() : "",
    volume: c.volume?.toString() || "",
    value: c.value?.toLocaleString("pt-BR", { minimumFractionDigits: 2 }) || "",
    unitPrice: c.unitValue?.toLocaleString("pt-BR", { minimumFractionDigits: 2 }) || "",
  }));
};

const ExpensesScreen = () => {
  const t = useT();
  const localizedName = useLocalizedName();
  const { locale } = useIntl();
  const momentLocale = resolveMomentLocale(locale);
  moment.locale(momentLocale);
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openModal, setOpenModal] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearchValue = useDebounce(searchValue, SEARCH_DEBOUNCE);

  const { startDate, endDate, isTreeReady, selectedVehicles, requestFilters, listFilters, previousMonthFilters } =
    useTransactionFilters();

  const kpiPeriodRange = formatPeriodRange(startDate, endDate);

  useEffect(() => {
    if (searchParams.get("add") === "expense") {
      setFile(null);
      setFileName("");
      setOpenModal(true);
      searchParams.delete("add");
      setSearchParams(searchParams, { replace: true });
    }

    const editId = searchParams.get("edit");
    if (editId) {
      handleEditExpense(editId);
      searchParams.delete("edit");
      setSearchParams(searchParams, { replace: true });
    }

    const detailId = searchParams.get("detail");
    if (detailId) {
      handleViewDetail(detailId);
      searchParams.delete("detail");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams]);
  const [editingExpenseId, setEditingExpenseId] = useState<string | null>(null);
  const [editingMaintenance, setEditingMaintenance] = useState<EditingMaintenance>(EMPTY_EDITING_MAINTENANCE);
  const [formInstanceKey, setFormInstanceKey] = useState(0);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [recurrenceDetailsId, setRecurrenceDetailsId] = useState<string | null>(null);
  const [deleteConfirmFileId, setDeleteConfirmFileId] = useState<string | null>(null);
  const [observation, setObservation] = useState({
    open: false,
    value: "",
  });
  const [auditExpenseId, setAuditExpenseId] = useState<string | null>(null);
  const [detailExpense, setDetailExpense] = useState<{
    open: boolean;
    data: any;
  }>({
    open: false,
    data: null,
  });
  const [file, setFile] = useState<File | null>(null);
  const [fileOdometer, setFileOdometer] = useState<File | null>(null);
  const [dialogOption, setDialogOption] = useState(defaultOption);
  const [fileName, setFileName] = useState("");
  const [fileNameOdometer, setFileNameOdometer] = useState("");
  const [attachmentUrl, setAttachmentUrl] = useState<string | null>(null);
  const [showDuplicateAlert, setShowDuplicateAlert] = useState(false);
  const [duplicateAlertMessage, setDuplicateAlertMessage] = useState("");
  const [reactivateItem, setReactivateItem] = useState<InactiveItem | null>(null);
  const [showReactivate, setShowReactivate] = useState(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [idOdometerPhoto, setIdOdometerPhoto] = useState<null | number>(null);
  const [tollPaymentMethods, setTollPaymentMethods] = useState<{ id: string; label: string }[]>([]);

  const {
    branchOptions,
    segmentsOptions,
    supplierOptions,
    driversOptions,
    categoriesOptions,
    vehiclesOptions,
    subCategoriesOptions,
    tripsOptions,
    trips,
    fetchTrips,
    setSelectedTrip,
    form,
    initialDefaultForm,
    setForm,
    fetchBranchOptions,
    fetchSegments,
    fetchSupplier,
    fetchCategories,
    fetchSubCategories,
  } = useExpensesForm();

  const fetchTollPaymentMethods = useCallback(async () => {
    try {
      const data = await getPaymentMethods();
      const options = data.map((item: any) => ({
        id: String(item.id),
        label: localizedName(item),
      }));
      setTollPaymentMethods(options);
    } catch {
      setTollPaymentMethods([]);
    }
  }, [localizedName]);

  useEffect(() => {
    fetchTollPaymentMethods();
  }, [fetchTollPaymentMethods]);

  const entityLabels: Record<string, string> = {
    branch: "filial",
    segment: "segmento",
    supplier: "fornecedor",
    category: "categoria",
    subcategory: "subcategoria",
  };

  const reactivateHandlers: Record<string, (id: number, activate: boolean) => Promise<any>> = {
    branch: inactivateBranch,
    segment: inactivateSegments,
    supplier: inactivateSuppliers,
    category: inactivateCategory,
    subcategory: inactivateSubCategories,
  };

  const refetchHandlers: Record<string, () => Promise<void>> = {
    branch: fetchBranchOptions,
    segment: fetchSegments,
    supplier: fetchSupplier,
    category: fetchCategories,
    subcategory: fetchSubCategories,
  };

  const handleInactiveSelect = (item: InactiveSelectOption, fieldName: string) => {
    setReactivateItem({
      id: item.id as string,
      name: item.label as string,
      fieldName,
    });
    setShowReactivate(true);
  };

  const handleReactivate = async (id: string, fieldName: string) => {
    const handler = reactivateHandlers[fieldName];
    if (!handler) return;

    await handler(Number(id), true);
    await refetchHandlers[fieldName]?.();

    setForm((prev) => ({
      ...prev,
      [fieldName]: id,
    }));

    Notification.success(
      `${entityLabels[fieldName]?.charAt(0).toUpperCase()}${entityLabels[fieldName]?.slice(1)} reativado(a) com sucesso.`
    );
  };

  const handleReactivateCancel = () => {
    setShowReactivate(false);
    setReactivateItem(null);
  };

  const onCreateNewOption = (name: NameInputChange) => {
    // Operacao/Segmento usa dialog especifico com 2 campos
    // if (name === 'segment') {
    //   setShowOperationDialog(true);
    //   return;
    // }

    const opt: Option = {
      branch: {
        placeholder: t("expense.createDialog.branch.placeholder"),
        title: t("expense.createDialog.branch.title"),
      },
      supplier: {
        placeholder: t("expense.createDialog.supplier.placeholder"),
        title: t("expense.createDialog.supplier.title"),
      },
      category: {
        placeholder: t("expense.createDialog.category.placeholder"),
        title: t("expense.createDialog.category.title"),
      },
      subcategory: {
        placeholder: t("expense.createDialog.subcategory.placeholder"),
        title: t("expense.createDialog.subcategory.title"),
      },
      segment: {
        placeholder: t("expense.createDialog.segment.placeholder"),
        title: t("expense.createDialog.segment.title"),
      },
      paymentMethod: {
        placeholder: t("expense.createDialog.paymentMethod.placeholder"),
        title: t("expense.createDialog.paymentMethod.title"),
      },
    };

    setDialogOption({
      open: true,
      placeholder: opt[name]?.placeholder,
      title: opt[name]?.title,
      type: name,
      value: "",
    });
  };

  const handleSaveBranch = async (value: string) => {
    try {
      const response = await createBranch({
        name: value,
      });
      fetchBranchOptions();
      setForm({
        ...form,
        branch: response.data.id.toString(),
      });
      setShowDuplicateAlert(false);
      setDialogOption(defaultOption);
      setOpenModal(true);
      Notification.success(t("expense.createDialog.branch.success"));
    } catch (error: any) {
      if (error?.response?.status === 409) {
        setDuplicateAlertMessage(t("expense.createDialog.branch.duplicate"));
        setShowDuplicateAlert(true);
      } else {
        Notification.error(t("expense.createDialog.branch.error"));
      }
    }
  };

  const handleSaveOperation = async (value: string) => {
    try {
      const response = await createSegments(value);
      fetchSegments();
      setForm({
        ...form,
        segment: response.data.id.toString(),
      });
      setDialogOption(defaultOption);
      Notification.success(t("expense.createDialog.segment.success"));
    } catch {
      Notification.error(t("expense.createDialog.segment.error"));
    }
  };

  const handleSaveSupplier = async (value: string) => {
    try {
      const response = await createSuppliers({
        name: value,
        type: "FORNECEDOR",
      });
      fetchSupplier();
      setForm({
        ...form,
        supplier: response.data.id.toString(),
      });
      setDialogOption(defaultOption);
      Notification.success(t("expense.createDialog.supplier.success"));
    } catch {
      Notification.error(t("expense.createDialog.supplier.error"));
    }
  };

  const handleSaveCategory = async (value: string, selectValue?: string) => {
    try {
      const response = await createCategory({
        name: value,
        type: "DESPESA",
        expenseType: selectValue as "DIRETO" | "OPERACIONAL",
      });
      fetchCategories();
      setForm({
        ...form,
        category: response.data.id.toString(),
      });
      setDialogOption(defaultOption);
      setOpenModal(true);
      Notification.success(t("expense.createDialog.category.success"));
    } catch {
      Notification.error(t("expense.createDialog.category.error"));
    }
  };

  const handleSaveSubCategory = async (value: string) => {
    try {
      const response = await createSubCategories(form.category, { name: value });
      fetchSubCategories();
      setForm({
        ...form,
        subcategory: response.data.id.toString(),
      });
      setDialogOption(defaultOption);
      Notification.success(t("expense.createDialog.subcategory.success"));
    } catch {
      Notification.error(t("expense.createDialog.subcategory.error"));
    }
  };

  const handleSavePaymentMethod = async (value: string) => {
    try {
      const response = await createPaymentMethod({
        id: null,
        name: value,
        displayOrder: 5,
        active: true,
        categoryIds: [510],
      });
      await fetchTollPaymentMethods();
      setForm({
        ...form,
        paymentMethod: String(response.data?.id ?? response.id),
      });
      setDialogOption(defaultOption);
      setOpenModal(true);
      Notification.success(t("expense.createDialog.paymentMethod.success"));
    } catch (error: any) {
      if (error?.response?.status === 409) {
        setDuplicateAlertMessage(t("expense.createDialog.paymentMethod.duplicate"));
        setShowDuplicateAlert(true);
      } else {
        Notification.error(t("expense.createDialog.paymentMethod.error"));
      }
    }
  };

  const handleSave = (value: string, selectValue?: string) => {
    if (dialogOption.type === "branch") {
      handleSaveBranch(value);
      return;
    }

    if (dialogOption.type === "supplier") {
      handleSaveSupplier(value);
      return;
    }

    if (dialogOption.type === "category") {
      handleSaveCategory(value, selectValue);
      return;
    }

    if (dialogOption.type === "subcategory") {
      handleSaveSubCategory(value);
      return;
    }
    if (dialogOption.type === "segment") {
      handleSaveOperation(value);
      return;
    }
    if (dialogOption.type === "paymentMethod") {
      handleSavePaymentMethod(value);
    }
  };

  const toVehicleRow = (item: TransactionTableItem): Vehicle => ({
    id: item.id.toString(),
    date: moment(item.dueDate || item.transactionDate).format("DD/MM/YYYY"),
    amount: item.amount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
    branch: item.branchName,
    segment: item.segmentBussinessName,
    customer: item.clientName,
    vehicle:
      item.vehiclePlate ||
      optionLabelToText(vehiclesOptions.find((v) => String(v.id) === String(item.vehicleId))?.label) ||
      null,
    driver: item.driverName,
    driverIds: item.driverIds || [],
    category: item.categoryName,
    subcategory: item.subcategoryName,
    taxes: `${item.taxPercentage}%`,
    status: resolveTransactionStatus(item.status, item.dueDate, item.transactionDate) as ExpenseStatus,
    recurrenceId: item.recurrenceId ?? null,
    source: item.source ?? null,
    trip: resolveTripLabel(item.trip, item.tripCode),
    tripId: item.trip?.id ?? item.tripCode ?? null,
    tripDriverId: resolveTripDriverId(item),
    paymentMethod: item.paymentMethod ?? null,
  });

  const calculateStatusTotals = (rows: Vehicle[]): Record<ExpenseStatus, number> => {
    const totals: Record<ExpenseStatus, number> = { previsto: 0, realizado: 0, vencido: 0 };
    rows.forEach((row) => {
      if (row.status) totals[row.status] += parseCurrencyBRL(row.amount);
    });
    return totals;
  };

  const selectSummaryStatusTotals = (
    data: any,
    period: TransactionStatusPeriod
  ): Record<ExpenseStatus, number> | null => {
    const amounts = resolveStatusAmounts(data?.expensesKpis, period);
    if (!amounts) return null;

    return { previsto: amounts.forecasted, realizado: amounts.realized, vencido: amounts.overdue };
  };

  const {
    tableData,
    totalRecords,
    isLoadingTable,
    isLoadingKpis,
    isLoadingMore,
    totalAmount,
    previousTotalAmount,
    previousKpiTotalsByStatus,
    kpiTotalsByStatus,
    kpiComparisons,
    fetchInitial,
    handleLoadMore,
  } = useTransactionListData<Vehicle, Record<ExpenseStatus, number>>({
    transactionType: "DESPESA",
    services: { getTableData, getDRESummary },
    startDate,
    endDate,
    isTreeReady,
    listFilters,
    previousMonthFilters,
    search: debouncedSearchValue,
    loadErrorMessage: t("expense.notification.loadError"),
    mapRow: toVehicleRow,
    calculateStatusTotals,
    selectSummaryTotal: (data) => data.totalDespesas,
    selectSummaryComparisons: (data) => data?.expensesKpis,
    selectSummaryStatusTotals,
  });

  const tableRows = useMemo(
    () =>
      tableData.map((row) => ({
        ...row,
        paymentMethod: resolvePaymentMethodLabel(row.paymentMethod, t, tollPaymentMethods),
      })),
    [tableData, t, tollPaymentMethods]
  );

  const recurrenceEdit = useRecurrenceEdit<FormState>({
    rows: tableData,
    successLabelKey: "recurrence.notification.editSuccess",
    errorLabelKey: "expense.notification.editError",
    onSaved: () => {
      setOpenModal(false);
      setEditingExpenseId(null);
      fetchInitial();
    },
    getAmount: (data) => parseCurrencyBRL(data.value),
    buildPayload: (data, scope, targetTransactionId) => ({
      scope,
      targetTransactionId,
      branchId: Number(data.branch),
      segmentBussinessId: Number(data.segment),
      supplierClientId: Number(data.supplier),
      categoryId: Number(data.category),
      subcategoryId: Number(data.subcategory),
      vehicleId: data.vehicle || null,
      driverIds: data.driver.length > 0 ? data.driver.slice(0, MAX_DRIVERS) : null,
      tripCode: data.trip || null,
      description: data.observation,
      paymentMethod: data.paymentMethod || null,
      taxPercentage: 0,
      status: toBackendStatus(data.status),
      amount: scope === "ALL_GROUP" ? undefined : parseCurrencyBRL(data.value),
      maintenance: buildGroupMaintenancePayload(data),
    }),
    submitSingle: (data, callback) => submitExpenseUpdate(data, callback),
  });

  const recurrenceDelete = useRecurrenceDelete({
    rows: tableData,
    successLabelKey: "expense.notification.deleteSuccess",
    errorLabelKey: "expense.notification.deleteError",
    onDeleted: fetchInitial,
  });

  const isFuelCategory = (categoryId: string) => {
    const selectedCategory = categoriesOptions.find((c) => c.id === categoryId);
    return normalizeText(selectedCategory?.rawName ?? (selectedCategory?.label as string)).includes("abastecimento");
  };

  const isTollCategory = (categoryId: string) => {
    const selectedCategory = categoriesOptions.find((c) => c.id === categoryId);
    return normalizeText(selectedCategory?.rawName ?? (selectedCategory?.label as string)).includes("pedagio");
  };

  const isMaintenanceCategory = (categoryId: string) => {
    const selectedCategory = categoriesOptions.find((c) => c.id === categoryId);
    return isMaintenanceCategoryName(selectedCategory?.rawName ?? (selectedCategory?.label as string));
  };

  const buildMaintenancePayload = (
    data: FormState,
    preserved?: Partial<ITransactionMaintenance> | null
  ): ITransactionMaintenance | undefined =>
    isMaintenanceCategory(data.category) && data.maintenanceType
      ? { ...preserved, maintenanceType: data.maintenanceType }
      : undefined;

  const buildGroupMaintenancePayload = (data: FormState): ITransactionMaintenance | undefined => {
    if (data.maintenanceType === editingMaintenance.initialType) return undefined;

    const { block } = editingMaintenance;
    const groupFields = block ? { executionType: block.executionType, service: block.service, plan: block.plan } : null;
    return buildMaintenancePayload(data, groupFields);
  };

  const resolveEditMaintenanceType = (expense: any): string => {
    if (expense?.maintenance?.maintenanceType) return expense.maintenance.maintenanceType;

    const isMaintenance =
      isMaintenanceCategoryName(expense?.categoryName) || isMaintenanceCategory(expense?.categoryId?.toString() ?? "");
    return isMaintenance ? resolveMaintenanceTypeFromSubcategory(expense?.subcategoryName) : "";
  };

  const resolveDueDate = (data: FormState): string | null =>
    isFuelCategory(data.category) || isTollCategory(data.category) ? null : data.dueDate || null;

  const buildTollStartDateTime = (date?: string, time?: string): string | undefined => {
    if (!date) return undefined;
    const datePart = extractDatePart(date);
    const timePart = time && /^\d{1,2}:\d{2}/.test(time) ? time.substring(0, 5) : "00:00";
    return `${datePart} ${timePart}:00.000`;
  };

  const buildTollPayload = (data: FormState) => {
    if (!isTollCategory(data.category)) return undefined;
    const startDateTime = buildTollStartDateTime(data.date, data.startTime);
    const payload: { axesCategory?: string; startDateTime?: string; plazaLatitude?: number; plazaLongitude?: number } =
      {};
    if (data.axesCategory) payload.axesCategory = data.axesCategory;
    if (startDateTime) payload.startDateTime = startDateTime;
    if (data.tollLatitude) payload.plazaLatitude = Number.parseFloat(data.tollLatitude);
    if (data.tollLongitude) payload.plazaLongitude = Number.parseFloat(data.tollLongitude);
    return Object.keys(payload).length > 0 ? payload : undefined;
  };

  const getFuelTypeFromSubcategory = (subcategoryId: string): string | undefined => {
    const subcategory = subCategoriesOptions.find((s) => s.id === subcategoryId);
    if (!subcategory) return undefined;
    const label = optionLabelToText(subcategory.label).toUpperCase();
    const fuelTypeMap: Record<string, string> = {
      DIESEL: "DIESEL",
      "DIESEL S10": "DIESEL_S10",
      "DIESEL S500": "DIESEL_S500",
      BIODIESEL: "BIODIESEL",
      GASOLINA: "GASOLINA",
      "GASOLINA ADITIVADA": "GASOLINA_ADITIVADA",
      ETANOL: "ETANOL",
      GNV: "GNV",
      ELETRICO: "ELETRICO",
      ELÉTRICO: "ELETRICO",
      ELECTRIC: "ELETRICO",
      HIBRIDO: "HIBRIDO",
      HÍBRIDO: "HIBRIDO",
    };
    return fuelTypeMap[label] || label || undefined;
  };

  const getFuelUnitFromSubcategory = (subcategoryId: string): string => {
    const subcategory = subCategoriesOptions.find((s) => s.id === subcategoryId);
    const normalized = optionLabelToText(subcategory?.label)
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
    if (normalized === "gnv") return "M³";
    if (normalized === "eletrico") return "kWh";
    return "L";
  };

  const formatDateTime = (date?: string, time?: string): string | null => {
    if (!date) return null;
    const formattedDate = extractDatePart(date);
    if (!time) return `${formattedDate}T00:00:00`;
    return `${formattedDate}T${time}:00`;
  };

  const toNumber = (val?: string): number => {
    if (!val) return 0;
    return Number(val.replace(",", ".")) || 0;
  };

  const buildSupplyPayload = (data: FormState): ISupplyTransactionPayload => {
    const complements = (data.complements || [])
      .filter((c) => c.type || c.volume || c.value || c.unitPrice)
      .map((c) => ({
        type: c.type || "",
        volume: toNumber(c.volume),
        value: parseCurrencyBRL(c.value) || 0,
        unitValue: parseCurrencyBRL(c.unitPrice) || 0,
      }));

    const transactionDate = data.startDate ? extractDatePart(data.startDate) : data.date;

    const tankCapacity = toNumber(data.tankCapacity) || undefined;
    const averageConsumption = toNumber(data.averageConsumption) || undefined;

    const supplyTrucker: ISupplyTrucker = {
      driverId: data.driver?.[0] || undefined,
      vehicleId: data.vehicle || undefined,
      startDateTime: formatDateTime(data.startDate, data.startTime) || undefined,
      stopLocation: data.stopLocation || undefined,
      locationCoordinates: data.locationCoordinates || undefined,
      odometer: data.odometerKm ? Number(data.odometerKm.replace(/\D/g, "")) : undefined,
      fuelType: getFuelTypeFromSubcategory(data.subcategory),
      fuelUnit: getFuelUnitFromSubcategory(data.subcategory),
      fuelVolume: toNumber(data.volume) || toNumber(data.totalVolume) || undefined,
      fuelUnitPrice:
        (data.fuelUnitPrice ? parseCurrencyBRL(data.fuelUnitPrice) : parseCurrencyBRL(data.totalUnitPrice ?? "")) ||
        undefined,
      fuelTotalPrice: parseCurrencyBRL(data.value ?? data.totalValue ?? "") || undefined,
      totalPaid: parseCurrencyBRL(data.value ?? data.totalValue ?? "") || undefined,
      paymentMethod: mapPaymentMethodFormToApi(data.paymentMethod) || undefined,
      notes: data.observation || undefined,
      vehicle: {
        id: data.vehicle || undefined,
        rioAssetId: data.vehicle || undefined,
        tankCapacity,
        averageConsumption,
      },
      complementsTransaction: complements.length > 0 ? complements : undefined,
    };

    return {
      branchId: Number(data.branch),
      segmentBussinessId: Number(data.segment) || undefined,
      supplierClientId: Number(data.supplier) || undefined,
      vehicleId: data.vehicle || undefined,
      driverId: data.driver?.[0] || undefined,
      driverIds: data.driver.length > 0 ? data.driver.slice(0, MAX_DRIVERS) : undefined,
      categoryId: Number(data.category),
      subcategoryId: Number(data.subcategory) || undefined,
      tripCode: data.trip || null,
      transactionDate,
      transactionType: "DESPESA",
      status: toBackendStatus(data.status),
      dueDate: resolveDueDate(data),
      amount: parseCurrencyBRL(data.value ?? data.totalValue ?? ""),
      quantity: toNumber(data.volume) || toNumber(data.totalVolume) || undefined,
      unitPrice:
        (data.fuelUnitPrice ? parseCurrencyBRL(data.fuelUnitPrice) : parseCurrencyBRL(data.totalUnitPrice ?? "")) ||
        undefined,
      taxPercentage: 0,
      paymentMethod: mapPaymentMethodFormToApi(data.paymentMethod) || undefined,
      description: data.observation || undefined,
      supplyTruckers: [supplyTrucker],
    };
  };

  const buildGenericPayload = (data: FormState): Partial<ITransactionPayload> => {
    if (!isFuelCategory(data.category)) return {};

    const complements = (data.complements || [])
      .filter((c) => c.type || c.volume || c.value || c.unitPrice)
      .map((c) => ({
        type: c.type || "",
        volume: toNumber(c.volume),
        value: parseCurrencyBRL(c.value) || 0,
        unitValue: parseCurrencyBRL(c.unitPrice) || 0,
      }));

    return {
      startDateTime: formatDateTime(data.startDate, data.startTime),
      stopLocation: data.stopLocation || null,
      locationCoordinates: data.locationCoordinates || null,
      odometer: data.odometerKm ? Number(data.odometerKm.replace(/\D/g, "")) : null,
      quantity: toNumber(data.volume) || toNumber(data.totalVolume) || undefined,
      unitPrice:
        (data.fuelUnitPrice ? parseCurrencyBRL(data.fuelUnitPrice) : parseCurrencyBRL(data.totalUnitPrice ?? "")) ||
        undefined,
      fuelVolume: toNumber(data.volume) || toNumber(data.totalVolume) || null,
      fuelUnit: getFuelUnitFromSubcategory(data.subcategory),
      fuelTotalPrice: parseCurrencyBRL(data.value ?? data.totalValue ?? "") || null,
      fuelUnitPrice:
        (data.fuelUnitPrice ? parseCurrencyBRL(data.fuelUnitPrice) : parseCurrencyBRL(data.totalUnitPrice ?? "")) ||
        null,
      totalPaid: parseCurrencyBRL(data.value ?? data.totalValue ?? "") || null,
      paymentMethod: mapPaymentMethodFormToApi(data.paymentMethod) || null,
      notes: data.observation || null,
      tankCapacity: toNumber(data.tankCapacity) || null,
      averageConsumption: toNumber(data.averageConsumption) || null,
      complements: complements.length > 0 ? complements : undefined,
    };
  };

  const getTransactionDate = (data: FormState) => {
    if (isFuelCategory(data.category) && data.startDate) {
      return extractDatePart(data.startDate);
    }
    return data.date;
  };

  const saveNewExpense = async (data: FormState, callback?: () => void, dialogModal = true) => {
    setIsLoadingSubmit(true);
    try {
      const isSupply = isFuelCategory(data.category);

      if (isSupply) {
        const supplyPayload = buildSupplyPayload(data);
        const response = await createSupplyTransaction(supplyPayload, file || undefined);
        await createOdometerTransaction(response.data.id, fileOdometer || undefined);
      } else {
        const genericFields = buildGenericPayload(data);
        const transactionDate = getTransactionDate(data);

        await createTransaction(
          {
            amount: parseCurrencyBRL(data.value),
            branchId: Number(data.branch),
            categoryId: Number(data.category),
            description: data.observation,
            driverIds: data.driver.length > 0 ? data.driver.slice(0, MAX_DRIVERS) : null,
            vehicleId: data.vehicle ? data.vehicle : null,
            segmentBussinessId: Number(data.segment),
            subcategoryId: Number(data.subcategory),
            supplierClientId: Number(data.supplier),
            taxPercentage: 0,
            transactionDate,
            tripCode: data.trip || null,
            transactionType: "DESPESA",
            status: toBackendStatus(data.status),
            dueDate: resolveDueDate(data),
            recurrence: buildRecurrencePayload(data),
            paymentMethod: data.paymentMethod || null,
            tollLocation: data.tollLocation,
            tollCoordinates: data.tollCoordinates || null,
            concessionaire: data.concessionaire,
            plaza: data.plaza || null,
            tagType: data.tagType,
            tagNumber: data.tagNumber,
            toll: buildTollPayload(data),
            maintenance: buildMaintenancePayload(data),
            ...genericFields,
          },
          file || undefined
        );
      }

      if (dialogModal) {
        setOpenModal(false);
      } else {
        setFile(null);
        setFileOdometer(null);
        setFileName("");
        setFileNameOdometer("");
        setAttachmentUrl(null);
      }

      setFile(null);
      setFileOdometer(null);
      setFileName("");
      setFileNameOdometer("");
      setAttachmentUrl(null);
      Notification.success(t("expense.notification.createSuccess"));
      callback?.();
      fetchInitial();
    } catch (error) {
      Notification.error(t(getTransactionSaveErrorLabelKey(error, "expense.notification.createError")));
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  const mapPaymentMethodApiToForm = (apiValue?: string | null): string => {
    if (apiValue == null || typeof apiValue !== "string") return "";
    const map: Record<string, string> = {
      CASH: "dinheiro",
      CREDIT: "credito",
      DEBIT: "debito",
      PIX: "pix",
      DINHEIRO: "dinheiro",
      CRÉDITO: "credito",
      DÉBITO: "debito",
    };
    return map[apiValue.toUpperCase()] ?? apiValue;
  };

  const mapPaymentMethodFormToApi = (formValue?: string | null): string | undefined => {
    if (!formValue) return undefined;
    const map: Record<string, string> = {
      credito: "CREDIT",
      debito: "DEBIT",
      dinheiro: "CASH",
      pix: "PIX",
    };
    return map[formValue.toLowerCase()] ?? formValue;
  };

  const fetchOdometerFile = async (id: string) => {
    try {
      const resOdometer = await getOdometerTransaction(id);
      const odometerPhoto = resOdometer?.data?.[0];

      setIdOdometerPhoto(odometerPhoto?.id ?? null);
    } catch (error) {
      console.log(error);
    }
  };

  const handleEditExpense = async (id: string) => {
    fetchOdometerFile(id);

    try {
      setFile(null);
      setFileOdometer(null);
      const response = await getDetailTransaction(id);
      const expense = response?.data ?? response;

      setFileName(expense?.fileNameAttachmentUrl || expense?.attachmentUrl || "");
      setAttachmentUrl(expense?.attachmentUrl || null);

      const maintenanceType = resolveEditMaintenanceType(expense);
      setEditingMaintenance({ block: expense?.maintenance ?? null, initialType: maintenanceType });

      const odometerPhoto = expense?.supplyTrucker?.odometerPhotos?.[0];
      setFileNameOdometer(odometerPhoto?.fileName || odometerPhoto?.url || "");

      // expense.driverIds vem como rioDriverId; o dropdown usa o id TMS interno. Traduz pra casar no select.
      const limitedDrivers = resolveEditDrivers(expense, driversOptions);
      const vehicleIdForForm = resolveEditVehicleId(expense, vehiclesOptions);

      const tollStartDateTime = expense?.toll?.startDateTime ?? expense?.tollStartDateTime;
      const { date: tollDate, time: tollTime } = splitTollDateTime(tollStartDateTime);

      const supply = expense.supplyTrucker;
      const startDateTime = supply?.start_date_time ?? supply?.startDateTime ?? expense.startDateTime;
      const stopLocation = supply?.stop_location ?? supply?.stopLocation ?? expense.stopLocation;
      const locationCoordinates =
        supply?.location_coordinates ??
        supply?.locationCoordinates ??
        expense.locationCoordinates ??
        expense.location_coordinates ??
        response?.locationCoordinates ??
        response?.location_coordinates ??
        "";
      const odometer = supply?.odometer ?? expense.odometer;
      // expense.quantity/unitPrice (raiz) refletem o último update; supplyTrucker.* fica stale após PUT.
      const fuelVolume = expense.quantity ?? expense.fuelVolume ?? supply?.fuel_volume;
      const fuelUnitPrice = expense.unitPrice ?? expense.fuelUnitPrice ?? supply?.fuel_unit_price;
      const notes = supply?.notes ?? expense.notes ?? expense.description;

      const rawTankCapacity =
        supply?.vehicle?.tank_capacity ??
        supply?.vehicle?.tankCapacity ??
        supply?.tank_capacity ??
        supply?.tankCapacity ??
        expense.tankCapacity ??
        expense.tank_capacity ??
        response?.tankCapacity ??
        response?.tank_capacity;

      const rawAverageConsumption =
        supply?.vehicle?.average_consumption ??
        supply?.vehicle?.averageConsumption ??
        supply?.average_consumption ??
        supply?.averageConsumption ??
        expense.averageConsumption ??
        expense.average_consumption ??
        response?.averageConsumption ??
        response?.average_consumption;

      const rawComplements = supply?.complements ?? expense.complementsTransaction ?? expense.complements;
      const complements = parseComplementsFromApi(rawComplements);

      const recurrenceForm = parseRecurrenceFromApi(expense?.recurrence, expense?.recurrenceId);

      setSelectedTrip(expense?.trip ?? null);

      setForm({
        status: resolveTransactionStatus(expense?.status, expense?.dueDate, expense?.transactionDate),
        dueDate: toFormDate(expense?.dueDate),
        source: expense?.source ?? "",
        ...recurrenceForm,
        date: tollDate || expense.transactionDate?.split("T")[0] || "",
        value: expense.amount?.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "",
        branch: expense.branchId?.toString() || "",
        segment: expense.segmentBussinessId?.toString() ?? "",
        supplier: expense.supplierClientId?.toString() ?? "",
        trip: expense?.trip?.id ?? expense?.tripCode ?? "",
        vehicle: vehicleIdForForm,
        driver: limitedDrivers,
        category: expense.categoryId?.toString() || "",
        subcategory: expense.subcategoryId?.toString() ?? "",
        maintenanceType,
        observation: notes || "",
        startDate: extractDateFromDateTime(startDateTime),
        startTime: tollTime || extractTimeFromDateTime(startDateTime),
        stopLocation: stopLocation || "",
        locationCoordinates: locationCoordinates || "",
        odometerKm: odometer?.toString() || "",
        volume: fuelVolume?.toString() || "",
        tankCapacity: rawTankCapacity != null ? String(rawTankCapacity) : "",
        averageConsumption: rawAverageConsumption != null ? String(rawAverageConsumption) : "",
        fuelUnitPrice: fuelUnitPrice?.toLocaleString("pt-BR", { minimumFractionDigits: 2 }) || "",
        paymentMethod: mapPaymentMethodApiToForm(expense.paymentMethod ?? supply?.payment_method),
        complements,
        tollLocation: expense?.tollLocation,
        tollCoordinates: expense?.tollCoordinates || "",
        tollLatitude: expense?.tollCoordinates ? (expense.tollCoordinates.split(",")[0] || "").trim() : "",
        tollLongitude: expense?.tollCoordinates ? (expense.tollCoordinates.split(",")[1] || "").trim() : "",
        concessionaire: expense?.concessionaire,
        plaza: expense?.toll?.plazaName || expense?.plaza || "",
        tagType: expense?.tagType,
        tagNumber: expense?.tagNumber,
        axesCategory: expense?.toll?.axesCategory ?? expense?.axesCategory ?? expense?.numberOfAxes ?? "",
        tollIsIntegration: expense?.toll?.isIntegration === true,
      });

      setEditingExpenseId(id);
      setOpenModal(true);
    } catch {
      Notification.error(t("expense.notification.loadDetailError"));
    }
  };

  const submitExpenseUpdate = async (data: FormState, callback?: () => void) => {
    if (!editingExpenseId) return;
    setIsLoadingSubmit(true);
    const isSupply = isFuelCategory(data.category);

    try {
      const genericFields = buildGenericPayload(data);
      const transactionDate = getTransactionDate(data);
      const response = await editTransaction(
        editingExpenseId,
        {
          amount: parseCurrencyBRL(data.value),
          branchId: Number(data.branch),
          categoryId: Number(data.category),
          description: data.observation,
          driverIds: data.driver.length > 0 ? data.driver.slice(0, MAX_DRIVERS) : null,
          vehicleId: data.vehicle ? data.vehicle : null,
          segmentBussinessId: Number(data.segment),
          subcategoryId: Number(data.subcategory),
          supplierClientId: Number(data.supplier),
          taxPercentage: 0,
          transactionDate,
          tripCode: data.trip || null,
          transactionType: "DESPESA",
          status: toBackendStatus(data.status),
          dueDate: resolveDueDate(data),
          recurrence: buildRecurrencePayload(data),
          paymentMethod: data.paymentMethod || null,
          tollCoordinates:
            data.tollLatitude && data.tollLongitude
              ? `${data.tollLatitude}, ${data.tollLongitude}`
              : data.tollCoordinates || null,
          tollLocation: data.tollLocation,
          concessionaire: data.concessionaire,
          plaza: data.plaza || null,
          tagType: data.tagType,
          tagNumber: data.tagNumber,
          toll: buildTollPayload(data),
          maintenance: buildMaintenancePayload(data, editingMaintenance.block),
          attachmentUrl: file ? undefined : attachmentUrl || null,
          fileNameAttachmentUrl: file ? undefined : fileName || null,
          ...genericFields,
        },
        file || undefined
      );

      if (isSupply) {
        if (!fileOdometer && !fileNameOdometer && idOdometerPhoto) {
          await deleteOdometerTransaction(editingExpenseId, idOdometerPhoto);
        } else if (fileOdometer && !idOdometerPhoto) {
          await createOdometerTransaction(response.data.id, fileOdometer);
        } else {
          await putOdometerTransaction(response.data.id, fileOdometer || undefined, idOdometerPhoto);
        }
      }

      setOpenModal(false);
      setEditingExpenseId(null);
      setFileOdometer(null);
      setFileNameOdometer("");
      Notification.success(t("expense.notification.editSuccess"));
      callback?.();
      fetchInitial();
    } catch (error) {
      Notification.error(t(getTransactionSaveErrorLabelKey(error, "expense.notification.editError")));
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  const handleUpdateExpense = async (expenseId: string, data: FormState, callback?: () => void) => {
    if (recurrenceEdit.requestEdit(expenseId, data, callback)) return;

    await submitExpenseUpdate(data, callback);
  };

  const handleDeleteExpense = async (id: string) => {
    if (recurrenceDelete.requestDelete(id)) return;

    setDeleteConfirmId(id);
  };

  const handleDeleteFileExpense = async (id: string) => {
    setDeleteConfirmFileId(id);
  };

  const confirmDeleteFileExpense = async () => {
    if (!deleteConfirmFileId) return;

    try {
      await deleteFile(deleteConfirmFileId);
      setDeleteConfirmFileId(null);
      Notification.success(t("expense.notification.fileDeleteSuccess"));
      fetchInitial();
    } catch {
      Notification.error(t("expense.notification.fileDeleteError"));
    }
  };

  const confirmDeleteExpense = async () => {
    if (!deleteConfirmId) return;

    try {
      await deleteTransaction(deleteConfirmId);
      setDeleteConfirmId(null);
      Notification.success(t("expense.notification.deleteSuccess"));
      fetchInitial();
    } catch {
      Notification.error(t("expense.notification.deleteError"));
    }
  };

  const openDownload = (url: string, delayMs = 0) => {
    setTimeout(() => {
      const a = document.createElement("a");
      a.href = url;
      a.download = "";
      document.body.appendChild(a);
      a.click();
      a.remove();
    }, delayMs);
  };

  const getDownloadFile = async (transactionId: string) => {
    try {
      const response = await getFile(transactionId);
      openDownload(response.data.downloadUrl);
      Notification.success(t("common.notification.downloadSuccess"));
    } catch {
      Notification.error(t("common.notification.downloadError"));
    }
  };

  const getDownloadOdometerFile = async (transactionId: string) => {
    try {
      const response = await getOdometerTransaction(transactionId);
      const downloadUrl = response?.data?.[0]?.downloadUrl;

      if (!downloadUrl) {
        Notification.error(t("expense.notification.odometerDownloadError"));
        return;
      }

      openDownload(downloadUrl);
      Notification.success(t("expense.notification.odometerDownloadSuccess"));
    } catch {
      Notification.error(t("expense.notification.odometerDownloadError"));
    }
  };

  const handleDownloadExpenseFile = async (transactionId: string, category: string) => {
    const isAbastecimento = String(category)
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .includes("abastecimento");

    if (!isAbastecimento) {
      return getDownloadFile(transactionId);
    }

    try {
      const [fileResponse, odometerResponse] = await Promise.all([
        getFile(transactionId).catch(() => null),
        getOdometerTransaction(transactionId).catch(() => null),
      ]);

      const mainUrl = fileResponse?.data?.downloadUrl;
      const odometerUrl = odometerResponse?.data?.[0]?.downloadUrl;

      if (mainUrl) openDownload(mainUrl, 0);
      if (odometerUrl) openDownload(odometerUrl, 300);

      if (mainUrl || odometerUrl) {
        Notification.success(t("expense.notification.bulkDownloadSuccess"));
      } else {
        Notification.error(t("expense.notification.bulkDownloadEmpty"));
      }
    } catch {
      Notification.error(t("expense.notification.bulkDownloadError"));
    }
  };

  const handleDefaultForm = () => {
    setForm(initialDefaultForm);
    setSelectedTrip(null);
    setEditingExpenseId(null);
    setFile(null);
    setFileName("");
    setFileOdometer(null);
    setFileNameOdometer("");
    setAttachmentUrl(null);
  };

  const handleObservationDefault = () => {
    setObservation({
      open: false,
      value: "",
    });
  };

  const handleDownload = async () => {
    try {
      await exportTransactions(startDate, endDate, "DESPESA");
      Notification.success(t("expense.notification.exportSuccess"));
    } catch {
      Notification.error(t("expense.notification.exportError"));
    }
  };

  const handleObservation = async (id: string) => {
    try {
      const response = await getDetailTransaction(id);
      setObservation({
        open: true,
        value: response?.data?.description,
      });
    } catch {
      Notification.error(t("expense.notification.detailError"));
      handleObservationDefault();
    }
  };

  const handleViewDetail = async (id: string) => {
    try {
      const response = await getDetailTransaction(id);
      const transaction = response?.data ?? response;
      setDetailExpense({
        open: true,
        data: typeof transaction === "object" && transaction !== null ? transaction : null,
      });
    } catch {
      Notification.error(t("expense.notification.detailError"));
      setDetailExpense({ open: false, data: null });
    }
  };

  const handleCloseDetail = () => {
    setDetailExpense({ open: false, data: null });
  };

  const handleSaveAndCreateNew = (data: FormState) => {
    saveNewExpense(
      data,
      () => {
        handleDefaultForm();
        setFormInstanceKey((prev) => prev + 1);
      },
      false
    );
  };

  const { currentMonth, onPressLeftPeriod, onPressRightPeriod } = useMonthPeriodNavigation(startDate);

  const handleClearFileOdometer = async () => {
    setFileOdometer(null);
  };

  const handleOpenModal = () => {
    setFile(null);
    setFileName("");
    setFileNameOdometer("");
    setFileOdometer(null);
    setAttachmentUrl(null);
    setOpenModal(true);
    setIdOdometerPhoto(null);

    if (!editingExpenseId) {
      const v = selectedVehicles.length === 1 ? selectedVehicles?.[0] : "";
      setForm((prev) => ({
        ...prev,
        vehicle: v,
      }));
    }
  };

  return (
    <div className="padding-10 padding-20-sm display-flex flex-column gap-10 gap-20-sm min-height-100pct">
      <MonthNavigation currentMonth={currentMonth} onPrevMonth={onPressLeftPeriod} onNextMonth={onPressRightPeriod} />
      <SelectedFilters requestFilters={requestFilters} />
      {isLoadingTable || isLoadingKpis ? (
        <KpiCardsSkeleton contentGapClassName="gap-10 gap-20-sm" />
      ) : (
        <div className="display-grid grid-cols-1 grid-cols-2-sm grid-cols-4-lg gap-10">
          <TransactionKpiCard
            label={t("expense.kpi.realized")}
            comparison={kpiComparisons.realized}
            formatComparisonValue={formatKpiComparisonCurrency}
            tooltip={
              <KpiInfoTooltip
                title={t("expense.kpi.realized")}
                description={t("expense.kpi.realized.tooltip")}
                lines={[kpiTotalsByStatus.realizado.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })]}
                periodRange={kpiPeriodRange}
              />
            }
            value={formatValue(kpiTotalsByStatus.realizado, false)}
            icon={CURRENCY_KPI_ICON}
            deltaPercentage={calculateDelta(kpiTotalsByStatus.realizado, previousKpiTotalsByStatus?.realizado)}
          />
          <TransactionKpiCard
            label={t("expense.kpi.forecast")}
            comparison={kpiComparisons.forecasted}
            formatComparisonValue={formatKpiComparisonCurrency}
            tooltip={
              <KpiInfoTooltip
                title={t("expense.kpi.forecast")}
                description={t("expense.kpi.forecast.tooltip")}
                lines={[kpiTotalsByStatus.previsto.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })]}
                periodRange={kpiPeriodRange}
              />
            }
            value={formatValue(kpiTotalsByStatus.previsto, false)}
            icon={CURRENCY_KPI_ICON}
            deltaPercentage={calculateDelta(kpiTotalsByStatus.previsto, previousKpiTotalsByStatus?.previsto)}
          />
          <TransactionKpiCard
            label={t("expense.kpi.overdue")}
            comparison={kpiComparisons.overdue}
            formatComparisonValue={formatKpiComparisonCurrency}
            tooltip={
              <KpiInfoTooltip
                title={t("expense.kpi.overdue")}
                description={t("expense.kpi.overdue.tooltip")}
                lines={[kpiTotalsByStatus.vencido.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })]}
                periodRange={kpiPeriodRange}
              />
            }
            value={formatValue(kpiTotalsByStatus.vencido, false)}
            icon={CURRENCY_KPI_ICON}
            deltaPercentage={calculateDelta(kpiTotalsByStatus.vencido, previousKpiTotalsByStatus?.vencido)}
          />
          <TransactionKpiCard
            label={t("expense.kpi.total")}
            comparison={kpiComparisons.total}
            formatComparisonValue={formatKpiComparisonCurrency}
            tooltip={
              <KpiInfoTooltip
                title={t("expense.kpi.total")}
                description={t("expense.kpi.total.tooltip")}
                lines={[totalAmount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })]}
                periodRange={kpiPeriodRange}
                placement="bottom-end"
              />
            }
            value={formatValue(totalAmount, false)}
            icon={CURRENCY_KPI_ICON}
            deltaPercentage={calculateDelta(totalAmount, previousTotalAmount ?? undefined)}
          />
        </div>
      )}
      <div className="flex-1-1 height-100pct">
        <TableExpenses
          searchValue={searchValue}
          onSearchValueChange={setSearchValue}
          data={tableRows}
          totalData={totalRecords}
          startDate={startDate}
          endDate={endDate}
          handleStartDate={(date) => dispatch(setStartDate(date))}
          handleEndDate={(date) => dispatch(setEndDate(date))}
          viewType="TABLE"
          onLoadMore={handleLoadMore}
          handleRefresh={fetchInitial}
          handleDownload={handleDownload}
          openModal={handleOpenModal}
          onEdit={handleEditExpense}
          onDelete={handleDeleteExpense}
          onDeleteFile={handleDeleteFileExpense}
          onGetFile={handleDownloadExpenseFile}
          onObservation={handleObservation}
          onAudit={(id) => setAuditExpenseId(id)}
          isLoadingMore={isLoadingMore}
          isLoadingTable={isLoadingTable}
        />

        <Dialog
          show={openModal && !dialogOption.open && !showReactivate}
          title={
            <span className="text-medium text-size-16">
              {editingExpenseId ? t("expense.dialog.editTitle") : t("expense.dialog.newTitle")}
            </span>
          }
          body={
            <ExpenseForm
              key={formInstanceKey}
              onViewRecurrence={() => setRecurrenceDetailsId(editingExpenseId)}
              branchs={branchOptions}
              segments={segmentsOptions}
              suppliers={supplierOptions}
              drivers={driversOptions}
              categories={categoriesOptions}
              vehicles={vehiclesOptions}
              trips={tripsOptions}
              tripsData={trips}
              onTripFiltersChange={fetchTrips}
              subCategories={subCategoriesOptions}
              fileName={fileName}
              changeFileName={(filename) => setFileName(filename)}
              fileNameOdometer={fileNameOdometer}
              changeFileNameOdometer={(filename) => setFileNameOdometer(filename)}
              form={form}
              setForm={setForm}
              openFormModal={(toggle) => {
                handleDefaultForm();
                setOpenModal(toggle);
              }}
              onCreateNewOption={onCreateNewOption}
              onSave={(formData) =>
                editingExpenseId
                  ? handleUpdateExpense(editingExpenseId, formData, handleDefaultForm)
                  : saveNewExpense(formData, handleDefaultForm)
              }
              isEdit={!!editingExpenseId}
              handleSubmitNew={handleSaveAndCreateNew}
              handleFile={(file) => setFile(file)}
              handleFileOdometer={(file) => setFileOdometer(file)}
              handleClearFile={() => {
                setFile(null);
                setAttachmentUrl(null);
              }}
              onInactiveSelect={handleInactiveSelect}
              handleClearFileOdometer={handleClearFileOdometer}
              isLoadingSubmit={isLoadingSubmit}
              tollPaymentMethods={tollPaymentMethods}
              transactionId={editingExpenseId ?? undefined}
              onDownloadFile={getDownloadFile}
              hasNewAttachment={!!file}
              onDownloadOdometerFile={getDownloadOdometerFile}
              hasNewOdometerAttachment={!!fileOdometer}
            />
          }
          onClose={() => {
            setOpenModal(false);
            handleDefaultForm();
          }}
          className="exampleDialog"
          showCloseButton
          bsSize="md"
        />

        <DialogCreateType
          title={dialogOption.title}
          show={dialogOption.type !== "category" && dialogOption.open}
          placeholder={dialogOption.placeholder}
          onClose={() => {
            setOpenModal(true);
            setDialogOption(defaultOption);
            setShowDuplicateAlert(false);
          }}
          onSave={(value) => handleSave(value)}
          duplicateAlertMessage={duplicateAlertMessage}
          onInputChange={() => setShowDuplicateAlert(false)}
          showDuplicateAlert={dialogOption.type === "branch" && showDuplicateAlert}
          existingNames={
            dialogOption.type === "subcategory"
              ? subCategoriesOptions.map((s) => optionLabelToText(s.label))
              : undefined
          }
          duplicateNameMessage={t("expense.createDialog.subcategory.duplicate")}
        />

        <DialogCreateCategory
          title={dialogOption.title}
          show={dialogOption.type === "category" && dialogOption.open}
          placeholder={dialogOption.placeholder}
          onClose={() => {
            setOpenModal(true);
            setDialogOption(defaultOption);
          }}
          onSave={handleSave}
          existingNames={categoriesOptions.map((c) => optionLabelToText(c.label))}
          duplicateNameMessage={t("expense.createDialog.category.duplicate")}
        />

        {/* <DialogCreateOperation
          show={showOperationDialog}
          onClose={() => { setShowOperationDialog(false); setOpenModal(true); }}
          onSave={handleSaveOperation}
        /> */}

        <DialogRecurrenceDetails
          show={recurrenceDetailsId !== null}
          module="DESPESA"
          transactionId={recurrenceDetailsId}
          recurrenceId={tableData.find((item) => item.id === recurrenceDetailsId)?.recurrenceId}
          onClose={() => setRecurrenceDetailsId(null)}
        />

        <RecurrenceScopeDialog
          show={recurrenceEdit.isOpen}
          mode="edit"
          isSubmitting={recurrenceEdit.isSaving}
          onClose={recurrenceEdit.cancelEdit}
          onConfirm={recurrenceEdit.confirmEdit}
        />

        <RecurrenceScopeDialog
          show={recurrenceDelete.target !== null}
          mode="delete"
          isSubmitting={recurrenceDelete.isDeleting}
          onClose={recurrenceDelete.cancelDelete}
          onConfirm={recurrenceDelete.confirmDelete}
        />

        <ConfirmDeleteDialog
          show={deleteConfirmId !== null}
          title={t("expense.dialog.deleteTitle")}
          message={t("expense.dialog.deleteConfirm")}
          onClose={() => setDeleteConfirmId(null)}
          onConfirm={confirmDeleteExpense}
        />

        <ConfirmDeleteDialog
          show={deleteConfirmFileId !== null}
          title={t("common.dialog.deleteReceiptTitle")}
          message={t("common.dialog.deleteReceiptConfirm")}
          onClose={() => setDeleteConfirmFileId(null)}
          onConfirm={confirmDeleteFileExpense}
        />

        <DialogObservation
          onClose={handleObservationDefault}
          show={observation.open}
          title={t("common.label.observations")}
          value={observation.value}
        />

        <DialogAudit
          show={!!auditExpenseId}
          onClose={() => setAuditExpenseId(null)}
          transactionId={auditExpenseId}
          entityLabel={t("expense.audit.label.expense")}
        />

        <ReactivateDialog
          show={showReactivate}
          item={reactivateItem}
          entityLabel={reactivateItem ? entityLabels[reactivateItem.fieldName] || "" : ""}
          transactionLabel="despesas"
          onReactivate={handleReactivate}
          onCancel={handleReactivateCancel}
        />

        <Dialog
          show={detailExpense.open}
          title={<span className="text-medium text-size-16">Detalhes da despesa</span>}
          body={
            detailExpense.data ? (
              <div className="padding-20 display-flex flex-column gap-10">
                <div className="display-flex justify-content-between">
                  <span className="text-color-gray">Data:</span>
                  <span>
                    {detailExpense.data.transactionDate
                      ? moment(detailExpense.data.transactionDate).format("DD/MM/YYYY")
                      : "-"}
                  </span>
                </div>
                <div className="display-flex justify-content-between">
                  <span className="text-color-gray">Valor:</span>
                  <span>
                    {detailExpense.data.amount?.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) || "-"}
                  </span>
                </div>
                <div className="display-flex flex-wrap justify-content-between">
                  <span className="text-color-gray">Filial:</span>
                  <span>{detailExpense.data.branchName || "-"}</span>
                </div>
                <div className="display-flex flex-wrap justify-content-between">
                  <span className="text-color-gray">Segmento:</span>
                  <span>{detailExpense.data.segmentBussinessName || "-"}</span>
                </div>
                <div className="display-flex flex-wrap justify-content-between">
                  <span className="text-color-gray">Fornecedor:</span>
                  <span>{detailExpense.data.supplierClientName || detailExpense.data.clientName || "-"}</span>
                </div>
                <div className="display-flex flex-wrap justify-content-between">
                  <span className="text-color-gray">Veículo:</span>
                  <span>{detailExpense.data.vehiclePlate || "-"}</span>
                </div>
                <div className="display-flex flex-wrap justify-content-between">
                  <span className="text-color-gray">Motorista:</span>
                  <span>{detailExpense.data.driverName || "-"}</span>
                </div>
                <div className="display-flex flex-wrap justify-content-between">
                  <span className="text-color-gray">Categoria:</span>
                  <span>{detailExpense.data.categoryName || "-"}</span>
                </div>
                <div className="display-flex flex-wrap justify-content-between">
                  <span className="text-color-gray">Subcategoria:</span>
                  <span>{detailExpense.data.subcategoryName || "-"}</span>
                </div>
                {detailExpense.data.description && (
                  <div className="display-flex flex-column gap-5 margin-top-10">
                    <span className="text-color-gray">Observação:</span>
                    <span className="">{detailExpense.data.description}</span>
                  </div>
                )}
              </div>
            ) : null
          }
          footer={
            <div className="display-flex flex-wrap gap-10 justify-content-end">
              <button className="btn btn-default width-100pct-xs" onClick={handleCloseDetail}>
                Fechar
              </button>
              <button
                className="btn btn-primary width-100pct-xs"
                onClick={() => {
                  handleCloseDetail();
                  if (detailExpense.data?.id) {
                    handleEditExpense(detailExpense.data.id.toString());
                  }
                }}
              >
                Editar
              </button>
            </div>
          }
          onClose={handleCloseDetail}
          showCloseButton
          bsSize="sm"
        />
      </div>
    </div>
  );
};

export default ExpensesScreen;
